/**
 * DPRPage - Pipeline Section Selection Page
 * 
 * This page displays pipelines for the selected project and allows
 * users to select a pipeline to view/create DPR logs.
 * 
 * Access at: /dpr?projectId=123
 * 
 * Layout: Uses ModernLayout which provides consistent left/right margins
 * Content order matches reference image exactly:
 * 1. Page Heading (Title + Subtext)
 * 2. Header Actions (Top Right: Create New Project, Settings, Dashboard)
 * 3. Back Navigation
 * 4. Project Header Row
 * 5. Divider
 * 6. Pipeline Section (using reusable PipelineSection component)
 */

import React, { useState, useEffect } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { connect, useDispatch, useSelector } from 'react-redux';
import { updateDprSkipState } from '../../redux/dpr/action';
import { Button, Toggle, Spinner, Checkbox } from '@crc-evans/ui-components';
import { DatePicker as AntDatePicker } from 'antd';
import { Settings, Plus, ChevronLeft, Clock, Shield, CheckCircle, TrendingUp, Users, Package, MessageSquare } from 'lucide-react';
import { FolderOutlined } from '@ant-design/icons';
import axiosConfig from '../../services/axiosConfig';
import { get } from 'lodash';
import PipelineSection from '../../components/DPR/PipelineSection';
import SafetyLog from '../../components/DPR/SafetyLog';
import QualityLog from '../../components/DPR/QualityLog';
import PersonnelLog from '../../components/DPR/PersonnelLog';
import ConsumablesLog from '../../components/DPR/ConsumablesLog';
import DowntimeLog from '../../components/DPR/DowntimeLog';
import ProductivityLog from '../../components/DPR/ProductivityLog';
import CommentsLog from '../../components/DPR/CommentsLog';
import moment from 'moment';
import './DPRPage.css';
import DPRSettings from '../../components/DPR/DPRSettings';

const Tabs = ({ items, activeTabId, onTabClick }) => {
  return (
    <div className="dpr-tabs">
      {items.map(item => (
        <div
          key={item.id}
          className={`dpr-tab-item ${activeTabId === item.id ? 'active' : ''}`}
          onClick={() => onTabClick(item.id)}
          style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
        >
          {item.icon}
          {item.label}
        </div>
      ))}
    </div>
  );
};

const DPRPage = (props) => {
  const location = useLocation();
  const history = useHistory();
  const [selectedProject, setSelectedProject] = useState(null);
  const [pipelineSections, setPipelineSections] = useState([]);
  const [isLoadingPipelines, setIsLoadingPipelines] = useState(false);

  // Report View State (declare before sowId / selectors that use selectedPipeline)
  const [selectedPipeline, setSelectedPipeline] = useState(null);
  const [activeTab, setActiveTab] = useState('safety');
  const [date, setDate] = useState(moment());
  const [shift, setShift] = useState('day');
  const [isLoadingDprLog, setIsLoadingDprLog] = useState(false);
  const [dprLogError, setDprLogError] = useState(null);
  const [showSettingsView, setShowSettingsView] = useState(false);
  const [activeSettingsTab, setActiveSettingsTab] = useState('reportConfig');

  const dispatch = useDispatch();
  const sowId = selectedPipeline?.id;

  // Select skip states
  const skipDay = useSelector(state =>
    sowId ? (state.dprReducer?.skipStatesBySection?.[sowId]?.skipDay || false) : false
  );
  const skipNight = useSelector(state =>
    sowId ? (state.dprReducer?.skipStatesBySection?.[sowId]?.skipNight || false) : false
  );
  const skipDpr = useSelector(state =>
    sowId ? (state.dprReducer?.skipStatesBySection?.[sowId]?.skipDpr || false) : false
  );

  const handleSkipChange = (key, e) => {
    const checked = e?.target ? e.target.checked : e;
    if (sowId) {
      dispatch(updateDprSkipState(sowId, key, checked));
    }
  };

  const tabItems = [
    { id: 'safety', label: 'Safety', icon: <Shield size={18} /> },
    { id: 'quality', label: 'Quality', icon: <CheckCircle size={18} /> },
    { id: 'productivity', label: 'Productivity', icon: <TrendingUp size={18} /> },
    { id: 'personnel', label: 'Personnel', icon: <Users size={18} /> },
    { id: 'consumables', label: 'Consumables', icon: <Package size={18} /> },
    { id: 'downtime', label: 'Downtime', icon: <Clock size={18} /> },
    { id: 'comments', label: 'Comments', icon: <MessageSquare size={18} /> },
  ];

  const shiftOptions = [
    { id: 'day', label: 'Day' },
    { id: 'night', label: 'Night' },
  ];

  // Get project data from URL params, Redux, or localStorage
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const projectId = params.get('projectId');

    // If projectId is in URL, find it from projectData
    if (projectId && props.projectData && Array.isArray(props.projectData)) {
      const project = props.projectData.find((p) => p.id === parseInt(projectId));
      if (project) {
        setSelectedProject(project);
        localStorage.setItem('selectedProject', JSON.stringify(project));
        fetchPipelineSections(project.id);
        return;
      }
    }

    // Fallback: Get from localStorage
    const storedProject = localStorage.getItem('selectedProject');
    if (storedProject) {
      try {
        const parsed = JSON.parse(storedProject);
        setSelectedProject(parsed);
        if (parsed.id) {
          fetchPipelineSections(parsed.id);
        }
      } catch (e) {
        console.error('Error parsing selectedProject from localStorage:', e);
      }
    }
  }, [location.search, props.projectData]);

  // Map GET /dpr-log response to commentsData shape for CommentsLog (same as old DPR.js)
  const getCommentsDataFromDprLog = (dprLogData) => {
    if (!dprLogData || typeof dprLogData !== 'object') return {};
    console.log('DPR Log Data for Comments:', dprLogData);
    const rd = dprLogData;
    const hasApprovalData =
      (rd.approvedBy1 && (rd.approvedBy1.teamMemberName || rd.approvedBy1.signature)) ||
      (rd.approvedBy2 && (rd.approvedBy2.teamMemberName || rd.approvedBy2.signature)) ||
      (rd.approvedBy3 && (rd.approvedBy3.teamMemberName || rd.approvedBy3.signature)) ||
      rd.managerSignatureS3Object ||
      rd.clientSignatureS3Object;
    const hasComments =
      (rd.dprDayShiftComments && rd.dprDayShiftComments.trim() !== '') ||
      (rd.dprNightShiftComments && rd.dprNightShiftComments.trim() !== '');
    if (rd.status === 'submitted' || hasApprovalData || hasComments) {
      return {
        dprDayShiftComments: rd.dprDayShiftComments || '',
        dprNightShiftComments: rd.dprNightShiftComments || '',
        managerName: rd.approvedBy1?.teamMemberName || '',
        clientName: rd.approvedBy2?.teamMemberName || '',
        approverName: rd.approvedBy3?.teamMemberName || '',
        managerSignature: rd.approvedBy1?.signature || rd.managerSignatureS3Object?.objectUrl || '',
        clientSignature: rd.approvedBy2?.signature || rd.clientSignatureS3Object?.objectUrl || '',
        approverSignature: rd.approvedBy3?.signature || rd.approverSignatureS3Object?.objectUrl || rd.approvedBy3?.s3Object?.objectUrl || rd.approverSignature || '',
        managerSignatureTimestamp: rd.approvedBy1?.signedAt || null,
        clientSignatureTimestamp: rd.approvedBy2?.signedAt || null,
        approverSignatureTimestamp: rd.approvedBy3?.signedAt || null,
      };
    }
    return {};
  };

  const fetchPipelineSections = async (projectId) => {
    setIsLoadingPipelines(true);
    try {
      const response = await axiosConfig.get(`/sows?projectId=${projectId}`);

      if (response.data && Array.isArray(response.data)) {
        setPipelineSections(response.data);
      } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
        setPipelineSections(response.data.data);
      } else {
        setPipelineSections([]);
      }
    } catch (error) {
      console.error('Error fetching pipeline sections:', error);
      setPipelineSections([]);
    } finally {
      setIsLoadingPipelines(false);
    }
  };

  // Fetch DPR log for selected pipeline/date (same as old workflow GET /dpr-log)
  const fetchDprLog = async (projectId, logCreatedBy, logDate, sowId) => {
    const params = new URLSearchParams({
      projectId: String(projectId),
      logCreatedBy: logCreatedBy || '',
      logDate: logDate,
      sowId: String(sowId),
    });
    const response = await axiosConfig.get(`/dpr-log?${params.toString()}`);
    const responseData = response?.data?.data ?? response?.data;
    return responseData;
  };

  const handlePipelineSelect = (pipeline) => {
    setSelectedPipeline(pipeline);
    setActiveTab('safety');
  };

  // Fetch DPR log when pipeline is selected or date changes in report view (GET /dpr-log)
  useEffect(() => {
    if (!selectedPipeline?.id || !selectedProject?.id) return;
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const logCreatedBy = user?.email || '';
    const logDate = moment(date).format('YYYY-MM-DD');
    const sowId = selectedPipeline.id;
    let cancelled = false;
    setIsLoadingDprLog(true);
    setDprLogError(null);
    fetchDprLog(selectedProject.id, logCreatedBy, logDate, sowId)
      .then((data) => {
        if (!cancelled) {
          setSelectedPipeline((prev) => ({
            ...prev,
            dprLogId: data?.id,
            dprLogData: data,
          }));
        }
      })
      .catch((error) => {
        if (!cancelled) {
          console.error('Error fetching DPR log:', error);
          setDprLogError(error?.response?.data?.message || 'Failed to load DPR log.');
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoadingDprLog(false);
      });
    return () => { cancelled = true; };
  }, [date, selectedPipeline?.id, selectedProject?.id]);

  // Sync skip day/night/DPR from API when dprLogData loads so checkboxes reflect saved state
  useEffect(() => {
    const data = selectedPipeline?.dprLogData;
    if (!selectedPipeline?.id || !data) return;
    const sowId = selectedPipeline.id;
    dispatch(updateDprSkipState(sowId, 'skipDay', Boolean(data.isSkippedDayShift)));
    dispatch(updateDprSkipState(sowId, 'skipNight', Boolean(data.isSkippedNightShift)));
    dispatch(updateDprSkipState(sowId, 'skipDpr', Boolean(data.isSkippedDpr)));
  }, [selectedPipeline?.id, selectedPipeline?.dprLogData, dispatch]);

  // Refetch DPR log and update selectedPipeline.dprLogData (so Comments and other sections show saved data on tab switch)
  const refetchDprLogForReportView = () => {
    if (!selectedPipeline?.id || !selectedProject?.id) return;
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const logCreatedBy = user?.email || '';
    const logDate = moment(date).format('YYYY-MM-DD');
    const sowId = selectedPipeline.id;
    fetchDprLog(selectedProject.id, logCreatedBy, logDate, sowId)
      .then((data) => {
        setSelectedPipeline((prev) => ({
          ...prev,
          dprLogId: data?.id,
          dprLogData: data,
        }));
      })
      .catch((err) => console.error('Error refetching DPR log after save:', err));
  };

  const handleBackClick = () => {
    if (selectedPipeline) {
      // Go back to pipeline list
      setSelectedPipeline(null);
    } else {
      // Navigate to dashboard
      history.push('/dashboard');
    }
  };

  const handleAddDpr = () => {
    console.log('Add DPR clicked');
  };

  if (!selectedProject) {
    return (
      <div className="dpr-page">
        <div className="dpr-loading" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px' }}>
          <Spinner variant="inline" size="medium" />
          <span>Loading project data...</span>
        </div>
      </div>
    );
  }

  // --- REPORT VIEW ---
  if (selectedPipeline) {
    return (
      <div className="dpr-page">
        {/* Header Section */}
        <div className="dpr-report-header">
          <div className="dpr-report-header-left">
            <h1 className="dpr-report-title">
              Section Report: {selectedPipeline.pipelineSection || selectedPipeline.name}
            </h1>
            <div className="dpr-report-subtitle">
              {tabItems.find(t => t.id === activeTab)?.label} Entry
            </div>
            <div
              className="dpr-back-link"
              onClick={handleBackClick}
            >
              <ChevronLeft size={16} /> Back To Sections
            </div>
          </div>

          <div className="dpr-report-header-right">
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <div style={{ display: 'flex', gap: '15px', marginRight: '10px' }}>
                <Checkbox
                  label="Skip Day Shift"
                  checked={skipDay}
                  onChange={(e) => handleSkipChange('skipDay', e)}
                  style={{ color: 'white' }}
                />
                <Checkbox
                  label="Skip Night Shift"
                  checked={skipNight}
                  onChange={(e) => handleSkipChange('skipNight', e)}
                  style={{ color: 'white' }}
                />
              </div>
              <div style={{ width: '150px' }}>
                <AntDatePicker
                  value={date}
                  onChange={setDate}
                  format="DD-MMM-YYYY"
                  allowClear={false}
                  disabledDate={(current) => current && current > moment().endOf('day')}
                  className="dpr-date-picker"
                  dropdownClassName="dpr-date-picker-dropdown"
                  style={{
                    width: '100%',
                    height: '36px',
                    borderRadius: '6px',
                    border: '1px solid #D1D5DB',
                    backgroundColor: '#FFFFFF',
                    color: '#374151'
                  }}
                  inputStyle={{
                    color: '#374151',
                    fontWeight: 500
                  }}
                />
              </div>
              <Toggle
                options={shiftOptions}
                activeId={shift}
                onToggle={setShift}
              />
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="dpr-report-tabs-container">
          <Tabs
            items={tabItems}
            activeTabId={activeTab}
            onTabClick={setActiveTab}
          />
        </div>

        {/* Content Area */}
        <div className="dpr-report-content">
          {dprLogError && (
            <div className="dpr-report-error" style={{ padding: '12px', marginBottom: '12px', backgroundColor: '#FEE2E2', color: '#DC2626', borderRadius: '6px' }}>
              {dprLogError}
            </div>
          )}
          {isLoadingDprLog && (
            <div className="dpr-report-loading" style={{ padding: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', color: '#666' }}>
              <Spinner variant="inline" size="medium" />
              <span>Loading DPR log...</span>
            </div>
          )}
          {!isLoadingDprLog && activeTab === 'safety' && (
            <SafetyLog
              dprLogId={selectedPipeline.dprLogId}
              sowId={selectedPipeline.id}
              shift={shift}
              date={date}
            />
          )}
          {!isLoadingDprLog && activeTab === 'quality' && (
            <QualityLog
              dprLogId={selectedPipeline.dprLogId}
              sowId={selectedPipeline.id}
              shift={shift}
              date={date}
            />
          )}
          {!isLoadingDprLog && activeTab === 'personnel' && (
            <PersonnelLog
              dprLogId={selectedPipeline.dprLogId}
              sowId={selectedPipeline.id}
              shift={shift}
            />
          )}
          {!isLoadingDprLog && activeTab === 'consumables' && (
            <ConsumablesLog
              dprLogId={selectedPipeline.dprLogId}
              sowId={selectedPipeline.id}
              shift={shift}
              projectId={selectedProject.id}
            />
          )}
          {!isLoadingDprLog && activeTab === 'downtime' && (
            <DowntimeLog
              dprLogId={selectedPipeline.dprLogId}
              sowId={selectedPipeline.id}
              shift={shift}
            />
          )}
          {!isLoadingDprLog && activeTab === 'comments' && (
            <CommentsLog
              commentsData={getCommentsDataFromDprLog(selectedPipeline.dprLogData)}
              dprLogId={selectedPipeline.dprLogId}
              dprStatus={selectedPipeline.dprLogData?.status ?? null}
              sowId={selectedPipeline.id}
              shift={shift}
              date={date}
              skipDayShift={skipDay}
              skipNightShift={skipNight}
              skipDpr={skipDpr}
              onSave={() => refetchDprLogForReportView()}
            />
          )}
          {/* Add other tabs placeholders */}
          {!isLoadingDprLog && activeTab === 'productivity' && (
            <ProductivityLog
              productivityData={selectedPipeline.dprLogData?.dprProductivity ?? {}}
              dprLogId={selectedPipeline.dprLogId}
              sowId={selectedPipeline.id}
              shift={shift}
              onSave={() => refetchDprLogForReportView()}
            />
          )}
          {!isLoadingDprLog && activeTab !== 'safety' && activeTab !== 'quality' && activeTab !== 'personnel' && activeTab !== 'consumables' && activeTab !== 'downtime' && activeTab !== 'comments' && activeTab !== 'productivity' && (
            <div className="dpr-tab-placeholder">
              {tabItems.find(t => t.id === activeTab)?.label} content coming soon.
            </div>
          )}
        </div>
      </div>
    );
  }

  // --- LIST VIEW (Project Selection) ---
  return (
    <div className="dpr-page">
      {/* Top Section: Page Heading (Left) + Header Actions (Right) */}
      <div className="dpr-page-top-section">
        {/* Page Heading Section - Left-aligned */}
        <div className="dpr-page-heading">
          <h1 className="dpr-page-title">{showSettingsView ? 'Settings' : 'Pipelines'}</h1>
          <p className="dpr-page-description">
            {!showSettingsView
              ? 'Select a pipeline section to view reports.'
              : activeSettingsTab === 'reportConfig'
                ? 'Configure which sections appear in the DPR report.'
                : activeSettingsTab === 'defectTypes'
                  ? 'Manage defect codes and descriptions.'
                  : 'Team settings and permissions.'
            }
          </p>
          {/* Back Navigation - Left-aligned, directly under subtitle */}
          <a
            href="#back"
            onClick={(e) => {
              e.preventDefault();
              if (showSettingsView) {
                setShowSettingsView(false);
              } else {
                handleBackClick();
              }
            }}
            className="dpr-page-back-link"
            style={{ marginTop: showSettingsView ? '24px' : undefined }}
          >
            <div
              className="dpr-back-link"
            >
              <ChevronLeft size={16} /> {showSettingsView ? 'Back to pipelines' : 'Back To Projects'}
            </div>
          </a>
        </div>
      </div>

      {/* Project Header Row - Hide when in settings view */}
      {!showSettingsView && (
        <div className="dpr-project-header-row">
          <div className="dpr-project-header-left">
            <FolderOutlined className="dpr-project-header-icon" />
            <span className="dpr-project-header-name">{selectedProject.projectName || selectedProject.name || 'Project'}</span>
          </div>
          <div className="dpr-project-header-actions">
            <Button
              label="Settings"
              variant="tertiary"
              size="small"
              icon={Settings}
              onClick={() => setShowSettingsView(true)}
            />
          </div>
        </div>
      )}

      {/* Divider - Hide when in settings view */}
      {!showSettingsView && <div className="dpr-divider"></div>}

      {/* Settings View - shown when user clicks Settings */}
      {showSettingsView ? (
        <DPRSettings
          activeTab={activeSettingsTab}
          setActiveTab={setActiveSettingsTab}
          projectId={selectedProject?.id}
        />
      ) : (
        <PipelineSection
          pipelines={pipelineSections}
          isLoading={isLoadingPipelines}
          onPipelineSelect={handlePipelineSelect}
        />
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  projectData: get(state, 'project.projectData', []),
});

export default connect(mapStateToProps)(DPRPage);
